/** `sizes` del hero de ficha — compartido entre preload SSR y next/image. */
export const PRODUCT_LCP_SIZES = "(max-width: 1023px) 70vw, 420px";

/**
 * Decide si una imagen puede pasar por el optimizador de Next.js.
 *
 * El optimizador corre del lado servidor (Vercel) y muchas CDNs de tienda
 * (p.ej. lacomer.com.mx) bloquean esas peticiones con anti-bot/hotlink: el
 * fetch del optimizador devuelve 502 y la imagen sale rota, aunque el navegador
 * SÍ puede cargar la URL original directamente.
 *
 * Regla: solo optimizamos imágenes alojadas en nuestro propio origen confiable
 * (R2 → *.sucom.com.mx / *.sucom.mx, o el R2_PUBLIC_HOSTNAME configurado) y los
 * assets locales. Todo lo demás se sirve `unoptimized` para que el navegador
 * cargue la URL original sin pasar por el optimizador bloqueado.
 */

const TRUSTED_HOST_SUFFIXES = ["sucom.com.mx", "sucom.mx"];

export function isOptimizableImageSrc(src: unknown): boolean {
  if (typeof src !== "string" || src.length === 0) return false;

  // Rutas locales (/placeholder.svg, /logos/…): mismo origen, optimizables.
  if (src.startsWith("/")) return true;

  let host: string;
  try {
    host = new URL(src).hostname.toLowerCase();
  } catch {
    // data: URIs u otros formatos no parseables: no optimizar.
    return false;
  }

  const publicHost = process.env.NEXT_PUBLIC_R2_PUBLIC_HOSTNAME?.toLowerCase();
  if (publicHost && host === publicHost) return true;

  return TRUSTED_HOST_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`)
  );
}

/** `unoptimized` para next/image: true cuando NO debemos optimizar. */
export function shouldSkipOptimization(src: unknown): boolean {
  return !isOptimizableImageSrc(src);
}

/**
 * Galería de producto: si ya hay imágenes en R2/local (optimizables), descarta
 * las URLs de tienda. Esas no pasan por `/_next/image` y Lighthouse descarga
 * el JPEG original (p.ej. 1000×1000 → ~50 KiB) solo para una miniatura 80px.
 * Si no queda ninguna URL confiable, conserva las externas.
 */
export function preferOptimizableImages(urls: readonly string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const url of urls) {
    if (typeof url !== "string" || !url || seen.has(url)) continue;
    seen.add(url);
    unique.push(url);
  }
  const trusted = unique.filter(isOptimizableImageSrc);
  return trusted.length > 0 ? trusted : unique;
}
